import React, { useState, useEffect } from 'react';
import { apiService, Country, PricingTier, CountryPricing } from '../services/api';

const Pricing: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countryPricing, setCountryPricing] = useState<CountryPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchCountryPricing(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const countries = await apiService.getAllCountries();
      
      if (countries && Array.isArray(countries)) {
        setCountries(countries);
        if (countries.length > 0) {
          setSelectedCountry(countries[0].code);
        }
      } else {
        setError('Invalid countries data received');
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
      setError('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryPricing = async (countryCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const countryPricingData = await apiService.getCountryPricing(countryCode);
      
      if (countryPricingData && countryPricingData.fixedPricing) {
        setCountryPricing(countryPricingData);
      } else {
        setError('Invalid response from pricing API');
      }
    } catch (err) {
      console.error('Failed to fetch country pricing:', err);
      setError('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = async (data: {
    countryCode: string;
    quantity: number;
    fixedPrice: number;
  }) => {
    try {
      await apiService.createPricingTier(data);
      fetchCountryPricing(selectedCountry);
    } catch (err: any) {
      console.error('Failed to create pricing tier:', err);
      const errorMessage = err?.message || 'Failed to create pricing tier';
      alert(errorMessage);
    }
  };

  const handleUpdateTier = async (tierId: string, data: {
    quantity?: number;
    fixedPrice?: number;
    isActive?: boolean;
  }) => {
    try {
      await apiService.updatePricingTier(tierId, data);
      fetchCountryPricing(selectedCountry);
      setEditingTier(null);
    } catch (err) {
      console.error('Failed to update pricing tier:', err);
      alert('Failed to update pricing tier');
    }
  };

  const handleDeleteTier = async (tierId: string) => {
    if (!window.confirm('Are you sure you want to delete this pricing tier?')) {
      return;
    }
    
    try {
      await apiService.deletePricingTier(tierId);
      fetchCountryPricing(selectedCountry);
    } catch (err) {
      console.error('Failed to delete pricing tier:', err);
      alert('Failed to delete pricing tier');
    }
  };

  if (loading && countries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading pricing data...</div>
      </div>
    );
  }

  if (error && countries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Fixed Pricing Management</h1>
        <p className="text-gray-600">Manage fixed prices for different quantities in each country</p>
      </div>

      {/* Country Selection */}
      <div className="mb-6">
        <label htmlFor="country-select" className="block text-sm font-medium text-gray-700 mb-2">
          Select Country
        </label>
        <select
          id="country-select"
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
        >
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>

      {loading && selectedCountry && (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-gray-600">Loading pricing for {selectedCountry}...</div>
        </div>
      )}

      {error && selectedCountry && (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-red-600">{error}</div>
        </div>
      )}

      {!countryPricing && !loading && selectedCountry && (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-gray-600">No pricing data available for this country.</div>
        </div>
      )}

      {countryPricing && !loading && (
        <div className="space-y-6">
          {/* Current Pricing Tiers */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Fixed Pricing for {countryPricing?.country?.name || selectedCountry}
            </h3>
            
            {!countryPricing.fixedPricing || countryPricing.fixedPricing.length === 0 ? (
              <p className="text-gray-500">No fixed pricing configured for this country.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fixed Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {countryPricing?.fixedPricing?.map((tier) => (
                      <tr key={tier.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {tier.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          €{tier.fixedPrice.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            tier.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {tier.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setEditingTier(tier)}
                            className="text-primary-600 hover:text-primary-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTier(tier.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add New Tier Form */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Fixed Pricing</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCreateTier({
                  countryCode: selectedCountry,
                  quantity: parseInt(formData.get('quantity') as string),
                  fixedPrice: parseFloat(formData.get('fixed_price') as string),
                });
                e.currentTarget.reset();
              }}
              className="grid grid-cols-1 gap-4 sm:grid-cols-3"
            >
              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                  Quantity (1-50)
                </label>
                <input
                  type="number"
                  name="quantity"
                  id="quantity"
                  required
                  min="1"
                  max="50"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="fixed_price" className="block text-sm font-medium text-gray-700">
                  Fixed Price (€)
                </label>
                <input
                  type="number"
                  name="fixed_price"
                  id="fixed_price"
                  required
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full btn-primary"
                >
                  Add Pricing
                </button>
              </div>
            </form>
          </div>

          {/* Edit Tier Modal */}
          {editingTier && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Edit Fixed Pricing
                  </h3>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleUpdateTier(editingTier.id, {
                        quantity: parseInt(formData.get('edit_quantity') as string),
                        fixedPrice: parseFloat(formData.get('edit_fixed_price') as string),
                        isActive: formData.get('edit_is_active') === 'true',
                      });
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="edit_quantity" className="block text-sm font-medium text-gray-700">
                        Quantity
                      </label>
                      <input
                        type="number"
                        name="edit_quantity"
                        id="edit_quantity"
                        defaultValue={editingTier.quantity}
                        required
                        min="1"
                        max="50"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit_fixed_price" className="block text-sm font-medium text-gray-700">
                        Fixed Price (€)
                      </label>
                      <input
                        type="number"
                        name="edit_fixed_price"
                        id="edit_fixed_price"
                        defaultValue={editingTier.fixedPrice}
                        required
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit_is_active" className="block text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <select
                        name="edit_is_active"
                        id="edit_is_active"
                        defaultValue={editingTier.isActive.toString()}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        className="flex-1 btn-primary"
                      >
                        Update Pricing
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingTier(null)}
                        className="flex-1 btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Pricing;
